<template>
  <div class="location-search">
    <div class="location-search__form">
      <div class="location-search__set-center-input">
        <ui-model-provider v-slot="{ value, updateValue }" :value.sync="searchTerm">
          <ui-input
            :placeholder="$t('wizard_step.centering.set_center_input_placeholder')"
            :value="value"
            required
            @input="updateValue"
          />
        </ui-model-provider>
      </div>
    </div>
    <div ref="attribution" />
    <div class="location-search__results">
      <p v-if="hasNoResults" class="location-search__no-results-warning u-reset">
        {{ $t('wizard_step.centering.set_center_no_results') }}
      </p>
      <ul v-if="locationResults.length" class="location-search__result-items u-reset">
        <li
          v-for="(locationResult, locationResultIdx) in locationResults"
          :key="locationResultIdx"
          class="location-search__result-item"
        >
          <button class="location-search__result" @click="onRequestDetails(locationResult.placeId)">
            {{ locationResult.description }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import { debounce } from 'lodash-es'
  import UiModelProvider from '@/components/form/ui-model-provider/ui-model-provider'
  import UiInput from '@/components/form/ui-input/ui-input'
  import { handleNotOk, TARGET_TYPES } from '@/components/location-search/util'

  export default {
    components: { UiInput, UiModelProvider },
    props: {
      rotation: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        isApiLoaded: false,
        isApiInitialized: false,
        isSearching: false,
        searchTerm: '',
        searchResults: [],
        GoogleMaps: null,
        AutocompleteSuggestions: null,
        Place: null,
      }
    },
    computed: {
      hasNoResults() {
        return !this.isSearching && !this.locationResults.length && this.searchTerm
      },
      locationResults() {
        return this.searchResults
          .filter(({ placePrediction }) => {
            return placePrediction.types.some((type) => TARGET_TYPES.includes(type))
          })
          .map(({ placePrediction }) => {
            const { placeId, text } = placePrediction
            return { placeId, description: text.text }
          })
      },
    },
    watch: {
      searchTerm() {
        this.isSearching = true
        this.debouncedSearchGeoLocation()
      },
      isApiLoaded() {
        this.Place = this.GoogleMaps.Place
        this.isApiInitialized = true
      },
    },
    async created() {
      const { Api } = await new Promise((resolve) => {
        require.ensure(
          [],
          () => {
            return Promise.all([
              require('./google-maps').default(this.$config.googleMapsApiKey),
            ]).then(([Api]) => resolve({ Api }))
          },
          null,
          'google-maps',
        )
      })

      this.GoogleMaps = Api
      this.AutocompleteSuggestions = this.GoogleMaps.AutocompleteSuggestion
      this.isApiLoaded = true
    },
    methods: {
      onRequestDetails(placeId) {
        this.getLocationDetails(placeId)
      },
      clearSearch() {
        this.searchTerm = ''
        this.searchResults = []
        this.isSearching = false
      },
      async searchGeoLocation() {
        if (!this.searchTerm) {
          this.clearSearch()
          return
        }

        try {
          const response = await this.AutocompleteSuggestions.fetchAutocompleteSuggestions({
            input: this.searchTerm,
          })
          this.isSearching = false
          this.searchResults = response.suggestions
        } catch (e) {
          handleNotOk(this.GoogleMaps, e)
        }
      },
      debouncedSearchGeoLocation: debounce(function () {
        this.searchGeoLocation()
      }, 400),
      async getLocationDetails(placeId) {
        if (!placeId) {
          return
        }

        const place = new this.Place({ id: placeId })

        try {
          const response = await place.fetchFields({ fields: ['location'] })
          const { lat, lng } = response.place.location.toJSON()
          this.$emit('update', [lng * -1, lat * -1, 0])
          this.clearSearch()
        } catch (e) {
          handleNotOk(this.GoogleMaps, e)
        }
      },
    },
  }
</script>

<style lang="scss" src="./_location-search.scss" />
